import {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../../shared/types';

export interface SelectProtocolModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (protocol: ISelectItem, version: ISelectItem) => void;
  protocols: ISelectItem[];
  versions: ISelectItem[];
  onProtocolChange: (protocol: ISelectItem | null) => void;
  onVersionChange?: (version: ISelectItem | null) => void;
  isDetailsReady?: boolean;
  description: string;
}

const SelectProtocolModal = ({
  open,
  onClose,
  onApply,
  protocols,
  versions,
  onProtocolChange,
  onVersionChange,
  isDetailsReady = true,
  description,
}: SelectProtocolModalProps) => {
  const [selectedProtocol, setSelectedProtocol] = useState<ISelectItem | null>(
    null
  );
  const [selectedVersion, setSelectedVersion] = useState<ISelectItem | null>(
    null
  );

  useEffect(() => {
    if (open) {
      return;
    }
    setSelectedProtocol(null);
    setSelectedVersion(null);
  }, [open]);

  const handleProtocolChange = (value: ISelectItem | null) => {
    setSelectedProtocol(value);
    setSelectedVersion(null);
    onProtocolChange(value);
    onVersionChange?.(null);
  };

  const handleVersionChange = (value: ISelectItem | null) => {
    setSelectedVersion(value);
    onVersionChange?.(value);
  };

  const handleClose = () => {
    onProtocolChange(null);
    onVersionChange?.(null);
    onClose();
  };

  const handleApply = () => {
    if (!selectedProtocol || !selectedVersion) {
      return;
    }
    onApply(selectedProtocol, selectedVersion);
    handleClose();
  };

  const isApplyDisabled =
    !selectedProtocol || !selectedVersion || !isDetailsReady;

  return (
    <ArrModal
      className="select-protocol-modal"
      showModal={open}
      onClose={handleClose}
      showClose={false}
      title={t('ai.review.newJobDetail.protocolSelection.modalTitle')}
      disableFocusTitle
      dataTestId="select-protocol-modal"
      buttons={[
        {
          id: 'select-protocol-cancel',
          label: t('common.cancel'),
          onClick: handleClose,
          variant: 'secondary',
          dataTestId: 'select-protocol-modal-cancel',
        },
        {
          id: 'select-protocol-apply',
          label: t('ai.review.newJobDetail.protocolSelection.applyProtocol'),
          onClick: handleApply,
          variant: 'primary',
          dataTestId: 'select-protocol-modal-apply',
          disabled: isApplyDisabled,
        },
      ]}
    >
      <Typography
        id="select-protocol-modal-description"
        className="typo-text body2_size text-body-strong"
        data-testid="select-protocol-modal-description"
      >
        {description}
      </Typography>
      <Box className="select-protocol-modal__fields">
        <ArrSingleSelectDropdown
          className="select-protocol-modal__dropdown"
          dataTestId="select-protocol-name-dropdown"
          dropdownLabel={t('ai.review.newJobDetail.selectProtocol')}
          dropdownItems={protocols}
          dropdownItemKey="id"
          dropdownItemDisplay="name"
          selectedValues={selectedProtocol}
          onSelectionChange={handleProtocolChange}
          sizeVariant="medium"
          searchable
          selectRestProps={{
            'aria-label': t('ai.review.newJobDetail.selectProtocol'),
            'aria-haspopup': 'listbox',
          }}
        />
        <ArrSingleSelectDropdown
          className="select-protocol-modal__dropdown"
          dataTestId="select-protocol-version-dropdown"
          dropdownLabel={t(
            'ai.review.protocolIntelligence.versionDropdownLabel'
          )}
          dropdownItems={versions}
          dropdownItemKey="id"
          dropdownItemDisplay="label"
          selectedValues={selectedVersion}
          onSelectionChange={handleVersionChange}
          sizeVariant="medium"
          searchable={false}
          disabled={!selectedProtocol}
          selectRestProps={{
            'aria-label': t(
              'ai.review.protocolIntelligence.versionDropdownLabel'
            ),
            'aria-haspopup': 'listbox',
          }}
        />
      </Box>
    </ArrModal>
  );
};

export default SelectProtocolModal;
